
import React from "react";
import type { AttachmentMeta } from "../types/attachments";

interface AttachmentPreviewModalProps {
  open: boolean;
  attachment: AttachmentMeta | null;
  onClose: () => void;
}

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  open,
  attachment,
  onClose,
}) => {
  if (!open || !attachment) return null;

  const { name, url, contentType } = attachment;
  const ct = (contentType ?? "").toLowerCase();

  const isImage =
    ct.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|svg)($|\?)/i.test(url);

  const isPdf =
    ct === "application/pdf" || /\.pdf($|\?)/i.test(url);

  const isWord =
    ct.includes("word") || /\.(docx?)($|\?)/i.test(url);

  const handleOpenInNewTab = () => {
    if (!url) return;
    // 在新視窗/分頁開啟，讓瀏覽器自行預覽或下載
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="lop-modal-backdrop" onClick={onClose}>
      <div className="lop-modal lop-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="lop-modal-header">
          <h3>預覽檔案：{name}</h3>
          <button
            type="button"
            onClick={onClose}
            className="lop-modal-close"
            aria-label="關閉預覽"
          >
            ×
          </button>
        </div>

        <div className="lop-modal-body">
          {isImage ? (
            <div
              className="lop-preview-wrapper"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxHeight: "70vh",
              }}
            >
              <img
                src={url}
                alt={name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : (
            <div
              className="lop-preview-fallback"
              style={{
                minHeight: "40vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "1.5rem",
              }}
            >
              {isPdf || isWord ? (
                <>
                  <p style={{ marginBottom: "0.5rem" }}>
                    此檔案類型（{isPdf ? "PDF" : "Word"}）在目前預覽環境下
                    可能無法內嵌顯示。
                  </p>
                  <p style={{ marginBottom: "1rem" }}>
                    請使用下方「在新視窗預覽」或「下載檔案」按鈕開啟。
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: "0.5rem" }}>
                    此檔案類型 ({contentType ?? "未知"}) 暫不支援線上預覽。
                  </p>
                  <p style={{ marginBottom: "1rem" }}>
                    仍可使用下方「下載檔案」按鈕取得原始檔案。
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div
          className="lop-modal-footer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
            paddingTop: "0.75rem",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="lop-secondary-button"
          >
            關閉
          </button>

          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="lop-primary-button"
          >
            在新視窗預覽
          </button>

          <a
            href={url}
            download={name}
            target="_blank"
            rel="noreferrer"
            className="lop-outline-button"
          >
            下載檔案
          </a>
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
